/**
 * Stan przedmiotu w pyszczku — czysta logika (testowalna bez Phaser).
 * PLAN-V2 §2.2 holdItem / heldItem.
 */

export type HoldItemState = {
  /** Klucz tekstury Phaser albo null (upuść). */
  textureKey: string | null;
  /** Klucz nadal renderowany w trakcie miękkiego dropu. */
  visualTextureKey: string | null;
  phase: 'empty' | 'pickup' | 'held' | 'drop';
  elapsedSec: number;
  durationSec: number;
};

export type HoldTransform = {
  alpha: number;
  angleDeg: number;
  offsetY: number;
  scale: number;
};

const PICKUP_DURATION_SEC = 0.18;
const DROP_DURATION_SEC = 0.24;

export function createHoldItemState(): HoldItemState {
  return {
    textureKey: null,
    visualTextureKey: null,
    phase: 'empty',
    elapsedSec: 0,
    durationSec: 0,
  };
}

/** null = upuść; pusty string traktuj jak null. */
export function holdItem(state: HoldItemState, textureKey: string | null): HoldItemState {
  if (textureKey == null || textureKey === '') {
    if (!state.visualTextureKey) return state;
    return {
      textureKey: null,
      visualTextureKey: state.visualTextureKey,
      phase: 'drop',
      elapsedSec: 0,
      durationSec: DROP_DURATION_SEC,
    };
  }
  if (state.textureKey === textureKey && state.phase !== 'drop') return state;
  return {
    textureKey,
    visualTextureKey: textureKey,
    phase: 'pickup',
    elapsedSec: 0,
    durationSec: PICKUP_DURATION_SEC,
  };
}

export function heldItem(state: HoldItemState): string | null {
  return state.textureKey;
}

export function visualHeldItem(state: HoldItemState): string | null {
  return state.visualTextureKey;
}

export function stepHoldTransition(
  state: HoldItemState,
  dtSec: number,
): HoldItemState {
  if (state.phase !== 'pickup' && state.phase !== 'drop') return state;
  const elapsedSec =
    state.elapsedSec + (Number.isFinite(dtSec) ? Math.max(0, dtSec) : 0);
  if (elapsedSec < state.durationSec) return { ...state, elapsedSec };
  return finishHoldTransition(state);
}

/** Natychmiastowy koniec przejścia — używany przy reduced-motion. */
export function finishHoldTransition(state: HoldItemState): HoldItemState {
  if (state.phase === 'pickup') {
    return {
      ...state,
      phase: 'held',
      elapsedSec: 0,
      durationSec: 0,
    };
  }
  if (state.phase === 'drop') return createHoldItemState();
  return state;
}

export function holdTransform(state: HoldItemState): HoldTransform {
  if (state.phase === 'empty' || state.phase === 'held') {
    return { alpha: 1, angleDeg: 0, offsetY: 0, scale: 1 };
  }
  const progress =
    state.durationSec > 0
      ? Math.min(1, Math.max(0, state.elapsedSec / state.durationSec))
      : 1;
  if (state.phase === 'drop') {
    const fall = progress * progress;
    return {
      alpha: 1 - progress,
      angleDeg: progress * 12,
      offsetY: fall * 18,
      scale: 1 - progress * 0.25,
    };
  }

  // Krótki snap z lekkim overshootem, bez niezależnego „latania” obiektu.
  const c1 = 1.70158;
  const c3 = c1 + 1;
  const p = progress - 1;
  const eased = 1 + c3 * p * p * p + c1 * p * p;
  return {
    alpha: 0.35 + progress * 0.65,
    angleDeg: (1 - progress) * -8,
    offsetY: (1 - eased) * 14,
    scale: 0.3 + eased * 0.7,
  };
}
